import { Axios } from "api/axios";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo, useState } from "react";
import SupplierViewDetailsModal from "../ViewDetails/SupplierViewDetailsModal";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import ActionButton from "components/Buttons/ActionButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportSupplierMasterCSV,
  fetchSupplierMaster,
} from "slices/Master/Supplier/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

const SupplierMaster = () => {
  document.title = "Supplier List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectPRs = createSelector(
    (state: any) => state.SupplierMasterReducer,
    (data) => ({
      list: data.list,
      rowCount: data.rowCount,
      loading: data.loading,
      error: data.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectPRs);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.suppliers
  );

  // Use preserved table state hook
  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = usePreservedTableState({
    key: "supplier",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const [data, setData] = useState([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [supplierDetails, setSupplierDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  useEffect(() => {
    dispatch(
      fetchSupplierMaster({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sortField, sortDirection, search, filterValue]);

  useEffect(() => {
    setData(list);
  }, [list]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await exportSupplierMasterCSV({
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      });

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export supplier data";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const handleStatusToggle = useCallback((row: any) => {
    if (!canUpdate) {
      toast.error("You Dont Have Permission to Change Status");
      return;
    }

    Axios.patch(`/master/supplier/update-status/${row?.supplier_id}/`)
      .then((success) => {
        toast.success(
          `Supplier Status ${row?.is_active ? "Deactivated" : "Activated"}`
        );
        dispatch(
          fetchSupplierMaster({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
          })
        );
      })
      .catch((error) => {
        console.log("Error : ", error);
        toast.error("Something Went Wrong");
      });
  }, [canUpdate, dispatch, pageIndex, pageSize, sortField, sortDirection, search, filterValue]);

  const handleViewDetails = useCallback((supplierId: string) => {
    setShowViewModal(true);
    setDetailsLoading(true);
    setSupplierDetails(null);

    Axios.get(`/master/supplier/${supplierId}/`)
      .then((response) => {
        if (response?.data?.data) {
          setSupplierDetails(response.data.data);
        } else {
          toast.error("Failed to fetch supplier details");
        }
      })
      .catch((err: any) => {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Failed to fetch supplier details");
        }
      })
      .finally(() => {
        setDetailsLoading(false);
      });
  }, []);

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSupplierDetails(null);
  };

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: canUpdate,
        onClick: (supplier: any) =>
          navigate(`/edit-supplier/${supplier.supplier_id}`, {
            state: supplier,
          }),
        color: "primary",
      },
      {
        icon: VisibilityIcon,
        label: "Details",
        permission: canView,
        onClick: (supplier: any) =>
          handleViewDetails(supplier.supplier_id),
        // onClick: (supplier: any) =>
        //   navigate(`/view-supplier/${supplier.supplier_id}`, {
        //     state: supplier,
        //   }),
        color: "info",
      },
    ];
    return items;
  }, [canUpdate, canView, navigate, handleViewDetails]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;

    if (!hasPermission) return [];

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canUpdate, canView, menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },
      {
        header: "Supplier Name",
        accessorKey: "supplier_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/supplier_name",
        cell: ({ row }: { row: any }) => {
          return row?.original?.supplier_name || "-";
        },
      },
      {
        header: "Supplier Code",
        accessorKey: "supplier_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/supplier_code",
        cell: ({ row }: { row: any }) => {
          return row?.original?.supplier_code || "-";
        },
      },

      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/email",
        cell: ({ row }: { row: any }) => {
          return row?.original?.email || "-";
        },
      },
      {
        header: "Contact No.",
        accessorKey: "phone_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/phone_no",
        cell: ({ row }: { row: any }) => {
          return row?.original?.phone_no || "-";
        },
      },
      {
        header: "GST No.",
        accessorKey: "gst_in",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/gst_in",
        cell: ({ row }: { row: any }) => {
          return row?.original?.gst_in || "-";
        },
      },
      {
        header: "Address",
        accessorKey: "address",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/address",
        cell: ({ row }: { row: any }) => {
          return <TruncatedTextCell
            text={row?.original?.address}
            maxWidth="200px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`address-tooltip-${row.index}`}
          />
        },
      },
      {
        header: "City",
        accessorKey: "city",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/city",
        cell: ({ row }: { row: any }) => {
          return row?.original?.city || "-";
        },
      },
      {
        header: "State",
        accessorKey: "state",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/state",
        cell: ({ row }: { row: any }) => {
          return row?.original?.state || "-";
        },
      },
      {
        header: "Pincode",
        accessorKey: "pincode",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/pincode",
        cell: ({ row }: { row: any }) => {
          return row?.original?.pincode || "-";
        },
      },
      {
        header: "Contact Person Name",
        accessorKey: "contact_person_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/contact_person_name",
        cell: ({ row }: { row: any }) => {
          return row?.original?.contact_person_name || "-";
        },
      },
      {
        header: "Contact Person Phone No.",
        accessorKey: "contact_person_phone_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/supplier/dropdown/contact_person_phone_no",
        cell: ({ row }: { row: any }) => {
          return row?.original?.contact_person_phone_no || "-";
        },
      },
      //  {
      //     header: "Remarks",
      //     accessorKey: "remark",
      //     enableColumnFilter: false,
      //     enableSorting: false,
      //     cell: ({ row }: { row: any }) => {
      //         const data = row.original;
      //         return `${data?.remark || '-'}`
      //     },
      // },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const mod = row.original;
          return (
            <StatusToggle
              isActive={mod.is_active}
              onChange={() => {
                handleStatusToggle(mod);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },

      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        // filterType: 'dropdown' as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      ...DynamicAction,
    ],
    [DynamicAction, handleStatusToggle, pageIndex, pageSize, canUpdate]
  );
  //   function handleChange(e: any): void {
  //     setSearchInput(e.target.value);
  //   }

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Supplier</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add Supplier"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-supplier"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setFilterValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search ..."
                  manualSorting={true}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* View Details Modal */}
      <SupplierViewDetailsModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        supplierDetails={supplierDetails}
        loading={detailsLoading}
      />
    </ListingPageContainer>
  );
};

export default SupplierMaster;
